<template>
  <div>
    <page-header
      :page="pageConfig"
      :actions="pageActionConfig"
      :left-side-class="'col-xl-8'"
      :right-side-class="
        'col-xl-4 align-items-end d-flex pb-2 justify-content-end'
      "
    />
    <Board :details="requestInfo" />
    <validation-observer ref="transferManagementRef" tag="form" novalidate>
      <div class="mt-4 pt-3">
        <section-title content="Mantenimiento de Admisión" has-line />
        <div class="row">
          <div class="col mt-2">
            <div class="bg-gray-9 p-2 rounded mt-4">
              <custom-radio
                v-model="model.selectedLevel"
                name="rbType"
                is-inline
                :options="rbOptions"
                class="custom-radio__extended"
                container-class="custom-radio-center"
                :disabled="isReadOnly"
                custom-error-msg=""
              />
            </div>
          </div>
          <div class="col">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-date-picker
                v-model="model.effectiveDate"
                placeholder="DD/MM/YYYY"
                label="Efectivo:"
                class="custom-date-picker__extended"
                :error="errors[0]"
                custom-error-msg="Favor seleccionar la fecha de efectividad."
                :disabled="isReadOnly"
              />
            </validation-provider>
          </div>
          <div class="col-6">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="model.selectedReason"
                fieldkey="id"
                fieldname="name"
                placeholder="Seleccione"
                label="Razón:"
                :clearable="false"
                :options="catalog.reasons"
                :error="errors[0]"
                custom-error-msg="Favor seleccionar la razón."
                :disabled="isReadOnly"
                @input="onSelectReasonChange"
              />
            </validation-provider>
          </div>
        </div>
        <div v-if="isInternTransfer" class="row mt-5 ml-3">
          <div class="col-12">
            <hr class="separator" />
          </div>
          <div class="col-12 mb-4" />
          <div class="col">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="model.selectedMunicipality"
                fieldkey="id"
                fieldname="name"
                placeholder="Seleccione"
                label="Municipio:"
                :clearable="false"
                :options="catalog.municipalities"
                :error="errors[0]"
                custom-error-msg="Favor seleccionar municipio."
                :disabled="isReadOnly"
                @input="onMunicipalityChange"
              />
            </validation-provider>
          </div>
          <div class="col">
            <validation-provider v-slot="{ errors }" rules="required">
              <custom-drop-down
                v-model="model.selectedLocation"
                fieldkey="id"
                fieldname="name"
                placeholder="Seleccione"
                label="Lugar de Servicio:"
                :clearable="false"
                :options="catalog.locations"
                :error="errors[0]"
                custom-error-msg="Favor seleccionar lugar de servicio."
                :disabled="isReadOnly"
                @input="onLocationChange"
              />
            </validation-provider>
          </div>
          <div class="col">
            <checkbox
              v-if="isEducationalService"
              v-model="model.isRequiredTeacher"
              class="mb-2"
              text="Maestro:"
              :disable="isReadOnly ? 'disable' : ''"
              @input="onSelectedTeacherCheck"
            />
            <validation-provider
              v-slot="{ errors }"
              :rules="specialistOrTeacherRequired"
            >
              <custom-drop-down
                v-model="model.selectedSpecialist"
                fieldkey="id"
                fieldname="name"
                placeholder="Seleccione"
                :label="!isEducationalService ? 'Especialista:' : ''"
                :clearable="false"
                :options="catalog.specialists"
                :error="errors[0]"
                :custom-error-msg="
                  'Favor seleccionar ' + specialistLabel.toLowerCase() + '.'
                "
                :disabled="isReadOnly || !model.isRequiredTeacher"
              />
            </validation-provider>
          </div>
          <div v-if="isEducationalService" class="col">
            <checkbox
              v-model="model.isRequiredAssistant"
              class="mb-2"
              :text="'Asistente de Servicio'"
              :disabled="!model.hasServiceAssistant || isReadOnly"
              @input="onSelectedAssistantCheck"
            />
            <validation-provider v-slot="{ errors }" :rules="assistantRequired">
              <custom-drop-down
                v-model="model.selectedServiceAssistant"
                fieldkey="id"
                fieldname="name"
                placeholder="Seleccione"
                :clearable="false"
                :options="catalog.serviceAssistants"
                :error="errors[0]"
                custom-error-msg="Favor seleccionar asistente de servicio."
                :disabled="isReadOnly || !model.isRequiredAssistant"
              />
            </validation-provider>
            <error
              v-if="!model.hasServiceAssistant"
              is-alert-mode
              message="No es posible seleccionar un asistente de servicio ya que no cuenta con uno en la solicitud."
            />
          </div>
        </div>
        <div class="row">
          <div
            v-if="!isInternTransfer && model.selectedReason && false"
            class="col-12 mt-5"
          >
            <section-title title="Documentos Anejados" has-line />
            <div
              v-for="(item, index) in documents"
              :key="`document-row-${index}`"
              class="row bg-secondary-3 rounded p-3 mb-3 ml-1 mr-0"
            >
              <div class="col-md-6">
                <div class="color-gray-6 font-14 font-weight-normal">
                  Tipo de Documento:
                </div>
                <div class="color-black font-16 font-weight-normal">
                  {{ item.documentTypeName }}
                </div>
              </div>
              <div class="col-md-6 d-flex justify-content-center">
                <span class="color-gray-6 font-14 font-weight-normal mr-3 mt-2">
                  Documento:
                </span>
                <validation-provider v-slot="{ errors }" rules="required">
                  <div class="d-flex align-items-center">
                    <custom-file-input
                      ref="attachedDocument"
                      v-model="item.selectedDocument"
                      :attachment-id="item.documentId"
                      :accept="'.pdf'"
                      :on-download="downloadFile"
                      required
                      display-style="normal"
                      label-position="left"
                      :error="errors[0]"
                      custom-error-msg="Favor de anejar un documento."
                      :disabled="item.documentId"
                    />
                    <div
                      v-if="item.selectedDocument && item.documentId"
                      class="color-gray-6 font-14 font-weight-normal ml-2"
                    >
                      {{ item.fileName }}
                    </div>
                  </div>
                </validation-provider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import objectToFormData from 'object-to-formdata';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import PageHeader from 'PageHeader';
import Board from 'Board';
import SectionTitle from 'SectionTitle';
import CustomRadio from 'custom-radio';
import Checkbox from 'CheckBox';
import CustomDatePicker from 'custom-date-picker';
import CustomDropDown from 'custom-drop-down';
import CustomFileInput from 'CustomFileInput';
import alertMixin from 'utils/mixins/alert.mixin.js';
import { Error } from '@/components/common';
import {
  getDropReasons as _getDropReasons,
  getAdmissionManagementMunicipalities as _getAdmissionManagementMunicipalities,
} from '@/modules/referral/api.js';

import {
  getTransferManagementLocations as _getTransferManagementLocations,
  getTransferManagementSpecialists as _getTransferManagementSpecialists,
  getTransferManagement as _getTransferManagement,
  saveAdmissionManagement as _saveAdmissionManagement,
} from '@/services/api/tapRequest.api.js';

import dropReasons from '@/utils/constants/pei/admissionDropReason.js';
import dropTypes from '@/utils/constants/pei/admissionDropType.js';
import documentsService from '@/services/documents';
import { tapRequestStatusesEnum } from '../_types';
import PeiAnalysisService from '@/utils/constants/pei/peiAnalysisService';
import studentApi from '@/services/student.js';

const DEFAULT_LEVEL_SELECTED = `${dropTypes.Traslado}`;
const INTERN_TRANSFER_IDENTIFIER = dropReasons.TrasladoInterno;

export default {
  name: 'TransferManagement',
  mixins: [alertMixin],
  components: {
    ValidationObserver,
    ValidationProvider,
    PageHeader,
    Board,
    SectionTitle,
    CustomRadio,
    CustomDatePicker,
    CustomDropDown,
    CustomFileInput,
    Checkbox,
    Error,
  },
  data: () => ({
    model: {
      selectedLevel: DEFAULT_LEVEL_SELECTED,
      selectedReason: null,
      selectedMunicipality: null,
      selectedLocation: null,
      selectedSpecialist: null,
      selectedServiceAssistant: null,
      serviceTypeId: null,
      isRequiredAssistant: false,
      isRequiredTeacher: false,
      hasServiceAssistant: false,
    },
    catalog: {
      reasons: [],
      municipalities: [],
      locations: [],
      specialists: [],
      serviceAssistants: [],
    },
    documents: [
      {
        documentTypeId: 100,
        documentTypeName: 'Informe de Alta / Baja (SAEE-SR-06)',
        selectedDocument: null,
        documentId: null,
      },
      {
        documentTypeId: 101,
        documentTypeName: 'Hoja de Trámite (SEE-UMESR-CSEE-VC-07)',
        selectedDocument: null,
        documentId: null,
      },
    ],
    requestId: null,
    academicYearId: null,
    companyId: null,
    statusId: null,
    isReadOnly: null,
    rbOptions: [{ label: 'Traslado', value: `${dropTypes.Traslado}` }],
    student: null,
  }),
  computed: {
    pageConfig() {
      return {
        icon: 'fas fa-user-edit',
        title: 'Manejo de Traslado',
        routes: [
          'Inicio',
          'Manejo de Solicitud de Asistencia Técnica de Ubicación del Estudiante',
          'Manejo de Traslado',
        ],
      };
    },
    pageActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.$router.go(-1),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'success',
          click: this.onSaveHandler,
          visible: !this.isReadOnly,
        },
      ];
    },
    requestInfo() {
      const schoolCode = this.student?.schoolCode ?? '';
      const schoolName = this.student?.schoolName ?? '';

      return [
        [
          {
            label: 'Número de Solicitud:',
            data: this.model?.requestNumber,
            icon: 'fas fa-ballot-check',
          },
          {
            label: 'Tipo de Servicio:',
            data: this.model?.serviceTypeName,
            icon: 'fas fa-ballot-check',
          },
          {
            label: 'Estudiante:',
            data: `${this.model?.studentSieId} - ${this.model?.studentName}`,
            icon: 'fas fa-address-book',
          },
          {
            label: 'Escuela',
            data: `${schoolCode} - ${schoolName}`,
            icon: 'fas fa-clipboard-list-check',
          },
          {
            label: 'Impedimento',
            data: this.student?.impediment,
            icon: 'fas fa-clipboard-list-check',
          },
        ],
      ];
    },
    isInternTransfer() {
      return this.model.selectedReason?.id == DEFAULT_LEVEL_SELECTED;
    },
    isEducationalService() {
      return this.model?.serviceTypeId == PeiAnalysisService.Educativo;
    },
    specialistLabel() {
      return this.isEducationalService ? 'maestro' : 'especialista';
    },
    specialistOrTeacherRequired() {
      return !this.isEducationalService ||
        !this.model.isRequiredAssistant ||
        this.model.isRequiredTeacher ||
        (!this.model.isRequiredTeacher && !this.model.isRequiredAssistant)
        ? 'required'
        : '';
    },
    assistantRequired() {
      return this.model.isRequiredAssistant ||
        !this.model.isRequiredTeacher ||
        (!this.model.isRequiredTeacher && !this.model.isRequiredAssistant)
        ? 'required'
        : '';
    },
  },
  async mounted() {
    const { id, academicYearId, companyId, statusId } = this.$route.params;
    this.requestId = id;
    this.academicYearId = academicYearId;
    this.companyId = companyId;
    this.statusId = statusId;

    await this.getTransferManagement();
    await this.getDropReasons();
    await this.getStudent(this.model?.studentSieId)
  },
  methods: {
    async getTransferManagement() {
      const { data } = await _getTransferManagement(this.requestId);
      if (data) {
        this.model = {
          ...this.model,
          ...data,
        };
      }
    },
    async getDropReasons() {
      if (this.statusId == tapRequestStatusesEnum.Notificado) {
        this.catalog.reasons = (
          await _getDropReasons(DEFAULT_LEVEL_SELECTED, 0, true)
        ).data.filter((x) => x.id != dropReasons.TrasladoInterno);
      } else {
        this.catalog.reasons = (
          await _getDropReasons(DEFAULT_LEVEL_SELECTED, 0, true)
        ).data;
      }
    },
    async getManagementMunicipalities() {
      const { data } = await _getAdmissionManagementMunicipalities(
        this.academicYearId,
        this.companyId
      );
      if (data) {
        this.catalog.municipalities = data.filter((x) => x.id > 0);
      }
    },
    async getTransferManagementLocations(municipalityId) {
      if (municipalityId) {
        this.catalog.locations = (
          await _getTransferManagementLocations(
            this.academicYearId,
            this.companyId,
            municipalityId
          )
        ).data;
      }
    },
    async getTransferManagementTeachers(municipalityId, locationId) {
      if (municipalityId && locationId) {
        let params = {
          academicYearId: this.academicYearId,
          companyId: this.companyId,
          municipalityId,
          locationId,
          isTeacher: true,
          isServiceAssistant: false,
        };

        this.catalog.specialists = await this.getTransferManagementSpecialists(
          params
        );
      }
    },
    async getTransferManagementServiceAssistants(municipalityId, locationId) {
      if (municipalityId && locationId) {
        let params = {
          academicYearId: this.academicYearId,
          companyId: this.companyId,
          municipalityId,
          locationId,
          isTeacher: false,
          isServiceAssistant: true,
        };

        this.catalog.serviceAssistants = await this.getTransferManagementSpecialists(
          params
        );
      }
    },
    async getTransferManagementSpecialists(params) {
      const { data } = await _getTransferManagementSpecialists(
        params.academicYearId,
        params.companyId,
        params.municipalityId,
        params.locationId,
        params.isTeacher,
        params.isServiceAssistant
      );

      return data;
    },
    async onSelectReasonChange(value) {
      if (value?.id === INTERN_TRANSFER_IDENTIFIER) {
        await this.getManagementMunicipalities();
      } else {
        this.model.selectedMunicipality = null;
        this.model.selectedLocation = null;
        this.model.selectedSpecialist = null;
      }
    },
    async onMunicipalityChange(value) {
      this.model = {
        ...this.model,
        selectedLocation: null,
        selectedSpecialist: null,
        selectedServiceAssistant: null,
      };
      this.catalog = {
        ...this.catalog,
        locations: [],
        specialists: [],
        serviceAssistants: [],
      };
      if (value?.id) {
        await this.getTransferManagementLocations(value?.id);
      }
    },
    async onLocationChange(value) {
      this.model = {
        ...this.model,
        selectedSpecialist: null,
        selectedServiceAssistant: null,
      };
      const municipalityId = this.model.selectedMunicipality?.id;
      if (value?.id && municipalityId) {
        await this.getTransferManagementTeachers(municipalityId, value?.id);
        await this.getTransferManagementServiceAssistants(
          municipalityId,
          value?.id
        );
      }
    },
    async onSelectedTeacherCheck(value) {
      if (!value) {
        this.model.selectedSpecialist = null;
      }
    },
    async onSelectedAssistantCheck(value) {
      if (!value) {
        this.model.selectedServiceAssistant = null;
      }
    },
    async onSaveHandler() {
      const isValid = await this.$refs.transferManagementRef.validate();

      if (
        !this.model.isRequiredAssistant &&
        !this.model.isRequiredTeacher &&
        this.model.selectedMunicipality != null &&
        this.model.selectedLocation != null &&
        this.model.effectiveDate != null &&
        this.model.selectedReason != null &&
        this.model.selectedReason != null
      ) {
        this.ShowToast(
          'Alerta',
          'Debe seleccionar al menos un maestro o un asistente de servicio.',
          'error'
        );
        return;
      }

      if (!isValid) {
        this.ShowToast('Alerta', 'Información Incompleta', 'error');
        return;
      }

      const params = {
        requestId: this.requestId,
        hasTransferLevel: this.model.selectedLevel != null,
        effectiveDate: this.model.effectiveDate,
        dropReasonId: this.model.selectedReason?.id,
        municipalityId: this.model.selectedMunicipality?.id,
        locationId: this.model.selectedLocation?.id,
        specialistId: this.model.selectedSpecialist?.id,
        ServiceAssistantId: this.model.selectedServiceAssistant?.id,
        files:
          this.model.selectedReason?.id === dropReasons.TrasladoInterno
            ? null
            : this.documents,
      };

      let formData = objectToFormData(params, {
        indices: true,
        nullsAsUndefineds: true,
      });

      await _saveAdmissionManagement(formData)
        .then((res) => {
          if (res.data) {
            this.ShowToast(
              '',
              'Los cambios fueron guardados exitosamente.',
              'success'
            );
            this.$router.go(-1);
          }
        })
        .catch((error) => {
          this.ShowToast('', error.response.data.message, 'error');
        });
    },
    downloadFile(attachmentId) {
      return documentsService.downloadFromServer(attachmentId);
    },
    async getStudent(sie) {
      const { data } = await studentApi.getBySie(sie);
      this.student = data;
    },
  },
};
</script>

<style scoped lang="scss">
@use "sass:map";
@import '@/assets/scss/variables/_colors.scss';

.separator {
  border-bottom: 1px solid map.get($colors, 'gray-5');
  border-top: 0;
  margin: 0;
}

.custom-radio__extended ::v-deep {
  .custom-radio-center {
    display: flex;
    justify-content: center !important;
    align-items: center !important;
  }
}

.custom-date-picker__extended ::v-deep {
  .custom-datetime-picker-container {
    width: 80% !important;
  }
}
</style>
